import { CommonProp } from '@acin/ui-core';
import React from 'react';
import {
  IInternalRegulationObligation,
  IInternalRegulationObligationRule,
  IInternalRegulationObligationRuleControl,
} from '../../../graphql/generated';

interface IContext {
  activeObligation: IInternalRegulationObligation | undefined;
  onUpdateActiveObligation: (obligation: IInternalRegulationObligation | undefined) => void;
  activeObligationRule: IInternalRegulationObligationRule | null;
  onUpdateActiveObligationRule: (rule: IInternalRegulationObligationRule | null) => void;
  activeObligationRuleControlMapping: IInternalRegulationObligationRuleControl | null;
  onUpdateActiveObligationRuleControlMapping: (mapping: IInternalRegulationObligationRuleControl | null) => void;
  // Rules whose control mappings have been confirmed via "Confirm & Next".
  confirmedRuleIds: Set<string>;
  onConfirmRule: (ruleId: string) => void;
  // Obligations whose rules have all been confirmed.
  reviewedObligationIds: Set<string>;
  onMarkObligationReviewed: (obligationId: string) => void;
}

export const INITIAL_STATE: IContext = {
  activeObligation: undefined,
  onUpdateActiveObligation: () => {},
  activeObligationRule: null,
  onUpdateActiveObligationRule: () => {},
  activeObligationRuleControlMapping: null,
  onUpdateActiveObligationRuleControlMapping: () => {},
  confirmedRuleIds: new Set(),
  onConfirmRule: () => {},
  reviewedObligationIds: new Set(),
  onMarkObligationReviewed: () => {},
};

export const Context = React.createContext(INITIAL_STATE);

export const useMapControls = () => React.useContext(Context);

export const MapControlsProvider: React.FC<CommonProp> = ({ children }) => {
  const [activeObligation, setActiveObligation] = React.useState<IInternalRegulationObligation | undefined>();
  const [activeObligationRule, setActiveObligationRule] = React.useState<IInternalRegulationObligationRule | null>(
    null,
  );
  const [activeObligationRuleControlMapping, setActiveObligationRuleControlMapping] =
    React.useState<IInternalRegulationObligationRuleControl | null>(null);
  const [confirmedRuleIds, setConfirmedRuleIds] = React.useState<Set<string>>(new Set());
  const [reviewedObligationIds, setReviewedObligationIds] = React.useState<Set<string>>(new Set());

  const onUpdateActiveObligation = React.useCallback(
    (obligation: IInternalRegulationObligation | undefined) => {
      setActiveObligation(obligation);
    },
    [setActiveObligation],
  );

  const onUpdateActiveObligationRule = React.useCallback(
    (rule: IInternalRegulationObligationRule | null) => {
      setActiveObligationRule(rule);
    },
    [setActiveObligationRule],
  );

  const onUpdateActiveObligationRuleControlMapping = React.useCallback(
    (mapping: IInternalRegulationObligationRuleControl | null) => {
      setActiveObligationRuleControlMapping(mapping);
    },
    [setActiveObligationRuleControlMapping],
  );

  const onConfirmRule = React.useCallback((ruleId: string) => {
    setConfirmedRuleIds((prev) => {
      const next = new Set(prev);
      next.add(ruleId);
      return next;
    });
  }, []);

  const onMarkObligationReviewed = React.useCallback((obligationId: string) => {
    setReviewedObligationIds((prev) => {
      const next = new Set(prev);
      next.add(obligationId);
      return next;
    });
  }, []);

  return (
    <Context.Provider
      value={{
        activeObligation,
        onUpdateActiveObligation,
        activeObligationRule,
        onUpdateActiveObligationRule,
        activeObligationRuleControlMapping,
        onUpdateActiveObligationRuleControlMapping,
        confirmedRuleIds,
        onConfirmRule,
        reviewedObligationIds,
        onMarkObligationReviewed,
      }}
    >
      {children}
    </Context.Provider>
  );
};
